import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import {
  ClientBrandCode,
  filterNullUndefined,
  QueryParametersService,
} from '@domgen/dgx-fe-common';
import { ComponentStore } from '@ngrx/component-store';
import { RegistrationFacade } from '@us-registration/data-access';
import {
  CmsApplianceDetailsSteppedPage,
  REGISTRATION_CONFIG,
  RegistrationConfig,
  RegistrationRequest,
  RegistrationRoutes,
} from '@us-registration/util-models';
import {
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { RegistrationQueryParams } from '../../constants/registration-query-params.enum';

export interface ViewModel {
  additionalContentVisible: boolean;
  clientBrandCode?: ClientBrandCode;
  cmsData: CmsApplianceDetailsSteppedPage;
}

interface ComponentState {
  additionalContentShownFieldCount: number;
  additionalContentVisible: boolean;
}

@Injectable()
export class ApplianceDetailsSteppedComponentService extends ComponentStore<ComponentState> {
  private readonly getQueryParamsUserToken$: Observable<string | undefined> =
    this.queryParametersService.get(RegistrationQueryParams.UserToken);

  private readonly additionalContentShownFieldCount$: Observable<number> =
    this.select(
      (state: ComponentState) => state.additionalContentShownFieldCount
    );

  private readonly additionalContentVisible$: Observable<boolean> = this.select(
    (state: ComponentState) => state.additionalContentVisible
  );

  readonly vm$: Observable<ViewModel> = combineLatest({
    additionalContentVisible: this.additionalContentVisible$,
    clientBrandCode: of(this.registrationConfig.clientBrandCode),
    cmsData: this.cmsContainer.content$,
  });

  constructor(
    @Inject(REGISTRATION_CONFIG)
    private readonly registrationConfig: RegistrationConfig,
    private readonly cmsContainer: CmsContainer<CmsApplianceDetailsSteppedPage>,
    private readonly registrationFacade: RegistrationFacade,
    private readonly router: Router,
    private readonly queryParametersService: QueryParametersService
  ) {
    super({
      additionalContentVisible: false,
      additionalContentShownFieldCount: 1,
    });
  }

  private readonly updateAdditionalContentVisible = this.updater(
    (state: ComponentState, additionalContentVisible: boolean) => ({
      ...state,
      additionalContentVisible: additionalContentVisible,
    })
  );

  readonly triggerAddUserToken = this.effect(
    (trigger$: Observable<string | undefined>) =>
      trigger$.pipe(
        filterNullUndefined(),
        tap((token) => {
          this.registrationFacade.addUserToken(token);
        })
      )
  )(this.getQueryParamsUserToken$);

  readonly triggerAdditionalContentVisible = this.effect(
    (trigger$: Observable<number>) =>
      trigger$.pipe(
        withLatestFrom(this.additionalContentShownFieldCount$),
        tap(([activeStep, additionalContentShownFieldCount]) => {
          const additionalContentVisible =
            activeStep <= additionalContentShownFieldCount;

          this.updateAdditionalContentVisible(additionalContentVisible);
        })
      )
  );

  private readonly onRegistrationFormSubmit = this.effect(
    (trigger$: Observable<RegistrationRequest>) =>
      trigger$.pipe(
        tap((registrationRequest: RegistrationRequest) =>
          this.registrationFacade.registerAppliance(registrationRequest)
        ),
        switchMap(() =>
          combineLatest([
            this.registrationFacade.hasRegisteredItem$,
            this.registrationFacade.hasRegisteredItemProducts$,
            this.registrationFacade.hasRegisteredItemQuoteNumber$,
            this.registrationFacade.hasRegisteredItemStandardPremium$,
          ])
        ),
        map(
          ([
            hasRegisteredItem,
            hasRegisteredItemProducts,
            quoteNumber,
            hasStandardPremium,
          ]) => {
            if (hasRegisteredItem) {
              if (
                hasRegisteredItemProducts &&
                quoteNumber &&
                hasStandardPremium
              ) {
                this.router.navigateByUrl(RegistrationRoutes.ProtectionDetails);
              } else {
                this.router.navigateByUrl(
                  RegistrationRoutes.RegistrationConfirmation
                );
              }
            }
          }
        )
      )
  );

  onFormSubmit(registrationRequest: RegistrationRequest): void {
    this.onRegistrationFormSubmit(registrationRequest);
  }
}
